import { Box, Chip } from "@mui/material";
import { underscoreToTitleCase } from "@utils/index";
import { useReportFilterStateContext } from "@hooks/use-report-filter";
import { useAssetsConfigContext } from "@hooks/use-config-context/asset";
import { isFieldDate } from "@hooks/use-config/utils";

interface ActiveFilterChipsProps {
  /**
   * Optional: List of enabled filter field names.
   * If provided, only these fields are shown as active filter chips.
   * If omitted (backward compatibility), all fields from the config are shown.
   */
  enabledFields?: readonly string[];
}

export const ActiveFilterChips = ({ enabledFields: enabledFieldsProp }: ActiveFilterChipsProps) => {
  const { filterState, createFilterSetter } = useReportFilterStateContext();
  const { config: assetsConfig } = useAssetsConfigContext();
  
  // Use prop if provided, otherwise show all fields in config
  const enabledFields = enabledFieldsProp ?? Object.keys(assetsConfig);

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2, "@media print": { display: "flex" } }}>
      {enabledFields.map((columnName) => {
        const fieldConfig = assetsConfig?.[columnName];
        if (!fieldConfig) return null;

        const value = filterState[columnName];
        const setter = createFilterSetter(columnName);
        const label = fieldConfig?.name || underscoreToTitleCase(columnName);

        // RENDER DATE RANGE CHIPS
        if (isFieldDate(fieldConfig)) {
          if (typeof value !== "object" || !("start" in value && "end" in value)) {
            return null;
          }

          if (!value.start && !value.end) return null;

          return (
            <Chip
              key={columnName}
              size="small"
              label={`${label}: ${value.start || "…"} – ${value.end || "…"}`}
              onDelete={() => setter({ start: null, end: null })}
              sx={{ "@media print": { "& .MuiChip-deleteIcon": { display: "none" } } }}
            />
          );
        }

        // RENDER ARRAY VALUE CHIPS
        if (!Array.isArray(value) || value.length === 0) {
          return null;
        }

        return value.map((item) => (
          <Chip
            key={`${columnName}-${item}`}
            size="small"
            label={`${label}: ${columnName === "asset_site" ? item : underscoreToTitleCase(item)}`}
            onDelete={() => setter(value.filter((v) => v !== item))}
            sx={{ "@media print": { "& .MuiChip-deleteIcon": { display: "none" } } }}
          />
        ));
      })}
    </Box>
  );
};
